import Paper from "@mui/material/Paper";
import PaymentInstallment from "components/PaymentInstallment/PaymentInstallment";
import { convertUTCToLocalDate } from "components/UTC/UtcConverter";
import * as calculation from "helper/CalculateEvent";
import { formatDate } from "helper/dateFormat";
import { numberFormat } from "helper/numberFormat";
import EventsService from "Lib/API/Events";
import { useSnackbar } from "notistack";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { eventsSlice, setTicketTypeVariants } from "services/api/events";
import { getAccessTokenSilently, getAuthenticated } from "utils/functions";
import {
  DataFoundWrapper,
  Discount,
  PayDetails,
  PaymentAmount,
  RetryButton,
  SpinnerIcon,
  SummaryItem,
  SummaryItemCharge,
  SummaryItemInfo,
  SummaryItemLedger,
  SummaryTitle,
  TotalDueAmount,
  TotalPayment,
  Wrapper,
} from "./Theme";
import { Fonts, FontColors } from "theme/fontConfig";

export const SummaryCard = ({
  handleTotalForPay,
  totalPaymentWithCVFee,
  billMyAccount,
  newProducts,
  setProducts,
  dueAmountCF,
  totalDueAmountWithCF,
  totalDiscount,
  dueAmountTotalDiscount,
  totalDueAmount,
  isInstallmentEnabled,
  setActivePaymentAfter,
  activePayment,
  setConvenienceFeeByPayment,
  convenienceFeeByPayment,
  currentPaymentMethod,
  selectedEventDates,
  selectedVariants,
  onCalcDueAmountChange,
  onCalcPriceStatusChange,
  formDataReservation,
  ticketsData,
  productsOpenAmount,
  donationAmount,
}) => {
  const {
    products,
    formOnlineData,
    varients,
    numberOfTickets,
    totalProducts,
    ticketTypeVariants,
    selectedValue,
  } = useSelector((state) => state?.Events);
  const { eventData } = useSelector((state) => state?.Events);
  const isIndividualRaw = eventData?.multiEventsType === "individual";
  const individualHasPricing = isIndividualRaw && !eventData?.freeEvent;
  const isIndividual = individualHasPricing;
  const totalPay = handleTotalForPay();
  const isAuthenticated = getAuthenticated();

  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [fetchingDiscount, setFetchingDiscount] = useState(false);
  const [refetchTrigger, setRefetchTrigger] = useState(0);
  const [calcResult, setCalcResult] = useState(null);
  const [apiFailed, setApiFailed] = useState(false);

  const triggerRefetch = () => setRefetchTrigger((t) => t + 1);

  // Extract only pricing-relevant parts of formDataReservation (slot IDs + ticket titles, not form field values)
  // so the calculate-price API doesn't re-fire on every keystroke in name/email inputs
  const slotTicketKeys = useMemo(
    () =>
      JSON.stringify(
        (formDataReservation || []).map((s) => ({ id: s.id, title: s.ticket?.title, ticketId: s.ticket?.id }))
      ),
    [formDataReservation]
  );

  // Extract only counter values from ticketsData (not open-amount prices, handled separately)
  const ticketCounterKeys = useMemo(
    () =>
      JSON.stringify(
        (ticketsData || []).map((slot) =>
          Array.isArray(slot) ? slot.map((p) => ({ id: p?._id, counter: p?.counter })) : []
        )
      ),
    [ticketsData]
  );

  const buildSlotProducts = ({ pr_list, selectedValueMap, slotIds, ticketsDataSlot, slotId, productsOpenAmount, newProducts, varients, ticketIndex, ticketsData }) => {
    return pr_list
      .filter((pr) => pr.type === "optional" || pr.type === "openAmount")
      .flatMap((pr) => {
        const isOpenAmount = pr.type === "openAmount" || pr.openAmount;
        if (isOpenAmount) {
          const priceIdx = pr_list
            .filter((p2) => p2.type === "optional" || p2.type === "openAmount")
            .findIndex((p2) => p2._id === pr._id);
          const slot = Array.isArray(ticketsDataSlot)
            ? ticketsDataSlot.find((p2) => p2?._id === pr._id)
            : ticketsDataSlot?.[priceIdx];
          const qty = slot?.counter || 0;
          if (qty <= 0) return [];
          const unitPrice = slotId && priceIdx !== -1 && productsOpenAmount?.[slotId]?.[priceIdx]
            ? Number(productsOpenAmount[slotId][priceIdx])
            : 0;
          if (unitPrice <= 0) return [];
          const newProduct = newProducts?.find((np) => np.priceId === pr._id);
          const obj = { id: pr._id, title: pr.title, quantity: qty, price: unitPrice };
          if (newProduct?.installmentStatus && newProduct?.installment?.value) obj.numberOfInstallments = newProduct.installment.value;
          return [obj];
        }
        const allEntries = selectedValueMap[pr.title] || [];
        const forThisSlot = slotIds.length > 0
          ? allEntries.filter((e) => slotIds.includes(e.ticketIndex))
          : allEntries;
        if (forThisSlot.length === 0) return [];
        if (pr.variant) {
          const variantMap = {};
          forThisSlot.forEach((e) => {
            if (!e.value || e.value === "") return;
            const count = (variantMap[e.value]?.quantity || 0) + 1;
            const variantEntry = Object.values(varients || {}).find((v) => v?.id === pr._id);
            const variantInfo = variantEntry?.variantsInfo?.find((v) => v.option === e.value);
            const isOpenAmountVariant = Boolean(variantInfo?.openAmount) || pr?.limitType === "amount";
            if (isOpenAmountVariant) {
              const price = variantInfo?.price ?? variantInfo?.amount ?? variantInfo?.minimumOpenAmount ?? 0;
              variantMap[e.value] = { quantity: count, price };
            } else {
              variantMap[e.value] = { quantity: count };
            }
          });
          if (Object.keys(variantMap).length === 0) return [];
          const variantObj = { id: pr._id, title: pr.title, quantity: forThisSlot.length, variant: variantMap };
          const newProduct = newProducts?.find((np) => np.priceId === pr._id);
          if (newProduct?.installmentStatus && newProduct?.installment?.value) variantObj.numberOfInstallments = newProduct.installment.value;
          return [variantObj];
        } else {
          const ticketProduct = Array.isArray(ticketsDataSlot)
            ? ticketsDataSlot.find((p2) => p2?._id === pr._id)
            : null;
          const counterQty = ticketProduct?.counter ?? 0;
          const selectedQty = forThisSlot.reduce((sum, e) => {
            const v = Number(e.value);
            return sum + (Number.isFinite(v) && v > 0 ? v : 0);
          }, 0);
          const totalQty = counterQty > 0 ? counterQty : selectedQty;
          if (totalQty === 0) return [];
          const obj = { id: pr._id, title: pr.title, quantity: totalQty };
          const newProduct = newProducts?.find((np) => np.priceId === pr._id);
          if (newProduct?.installmentStatus && newProduct?.installment?.value) obj.numberOfInstallments = newProduct.installment.value;
          return [obj];
        }
      });
  };

  useEffect(() => {
    if (!eventData?._id) return;
    let cancelled = false;
    const fetchCalcPrice = async () => {
      setFetchingDiscount(true);
      try {
        const token =
          (await getAccessTokenSilently()) ||
          localStorage.getItem("eventToken") ||
          localStorage.getItem("schoolToken") ||
          localStorage.getItem("formToken");
        if (!token || cancelled) return;

        const isGroup = eventData?.multiEventsType === "grouped";
        const isIndividualCalc = eventData?.multiEventsType === "individual";

        // For individual multi-date: one entry per slot so selectedEventDates[slotIdx] maps directly
        // Two UI modes exist:
        //   1. variant-based (hasMultiDateVariants): selectedVariants[slotIdx] is a map of dayKey→variantKey
        //   2. checkbox-based (!hasMultiDateVariants): selectedEventDates[slotIdx] is an array of multiEventsDates indices
        const onRegPrice = eventData?.eventPricing?.prices?.find((p) => p?.type === "onRegistration" && p?.variant);
        const rawVariantInfo = onRegPrice?.variantInfo || {};
        const hasMultiDateVariants = Object.values(rawVariantInfo).some((v) => v?.eventDate);

        // ticketTypeVariants[0].selectedQuantity is set to numberOfTickets.length just before navigation,
        // so it's the most reliable count — formDataReservation may lag behind on first render.
        const indivCount = isIndividualCalc
          ? Math.max(
              (ticketTypeVariants?.[0]?.selectedQuantity) || 0,
              (numberOfTickets || []).length,
              (formDataReservation || []).length,
            )
          : 0;
        const indivSlots = isIndividualCalc
          ? Array.from(
              { length: indivCount },
              (_, i) => (formDataReservation || [])[i] ?? (formDataReservation || [])[0] ?? {},
            )
          : [];

        const registrationForm = isIndividualCalc
          ? indivSlots.map((slot, slotIdx) => {
              const ticketVariantEntry = (ticketTypeVariants || []).find(
                (t) =>
                  (t._id === (slot?.ticket?.id || onRegPrice?._id) &&
                    t.name?.toLowerCase() === slot?.ticket?.title?.toLowerCase()) ||
                  t._id === (slot?.ticket?.id || onRegPrice?._id),
              );
              const ticket = {
                id: slot?.ticket?.id || onRegPrice?._id,
                title: slot?.ticket?.title,
              };
              if (ticketVariantEntry?.installmentStatus && ticketVariantEntry?.installment?.value) {
                ticket.numberOfInstallments = ticketVariantEntry.installment.value;
              }
              let eventsDates = [];
              if (hasMultiDateVariants) {
                // Variant path: selectedVariants[slotIdx] = { dayKey: variantKey }
                // dayKey = ISO date prefix (16 chars), variantKey = full __mde_... key
                const variantMap = (selectedVariants?.[slotIdx]) || {};
                // Group rawVariantInfo by dayKey to detect multi-variant days
                const dayKeyGroups = {};
                Object.entries(rawVariantInfo).forEach(([vKey, vVal]) => {
                  if (!vVal?.eventDate) return;
                  const dk = vVal.eventDate.substring(0, 16);
                  if (!dayKeyGroups[dk]) dayKeyGroups[dk] = [];
                  dayKeyGroups[dk].push(vKey);
                });
                eventsDates = Object.entries(variantMap)
                  .map(([dayKey, variantKey]) => {
                    const vInfo = rawVariantInfo[variantKey];
                    if (!vInfo?.eventDate) return null;
                    const matchingDate = (eventData?.multiEventsDates || []).find(
                      (md) => md?.eventDate?.substring(0, 16) === dayKey
                    );
                    const label = matchingDate?.label || matchingDate?.variantName || matchingDate?.name || "";
                    const isMulti = (dayKeyGroups[dayKey] || []).length > 1;
                    const entry = { date: vInfo.eventDate, name: variantKey, label };
                    if (isMulti) {
                      const variantDisplayName = vInfo.variantLabel || vInfo.dateLabel;
                      if (variantDisplayName) entry.variant = variantDisplayName;
                    }
                    return entry;
                  })
                  .filter(Boolean);
              } else {
                // Checkbox path: selectedEventDates[slotIdx] is array of indices into multiEventsDates
                eventsDates = (selectedEventDates?.[slotIdx] || [])
                  .map((idx) => {
                    const d = eventData?.multiEventsDates?.[idx];
                    return d ? { date: d.eventDate, name: d.name || d.label, label: d.label || d.name } : null;
                  })
                  .filter(Boolean);
              }
              const ticketProducts = buildSlotProducts({
                pr_list: eventData?.eventPricing?.prices || [],
                selectedValueMap: selectedValue?.[0] || {},
                slotIds: [slot.id],
                ticketsDataSlot: ticketsData?.[slotIdx],
                slotId: slot.id,
                productsOpenAmount,
                newProducts,
                varients,
                ticketIndex: slotIdx,
                ticketsData,
              });
              return { ticket, products: ticketProducts, eventsDates };
            })
          : isGroup
          ? (ticketTypeVariants || [])
              .filter((t) => t.selectedQuantity > 0)
              .flatMap((t) => {
                const slotsForTicket = (formDataReservation || []).filter(
                  (slot) => slot?.ticket?.title === t.name
                );
                const selectedValueMap = selectedValue?.[0] || {};
                const eventsDates = (eventData?.multiEventsDates || []).map((d) => ({
                  date: d.eventDate,
                  name: d.name || d.label,
                  label: d.label || d.name,
                }));
                return Array.from({ length: t.selectedQuantity }, (_, instanceIdx) => {
                  const ticket = { id: t._id, title: t.name };
                  if (t.installmentStatus && t.installment?.value) {
                    ticket.numberOfInstallments = t.installment.value;
                  }
                  const slot = slotsForTicket[instanceIdx];
                  const slotId = slot?.id;
                  const globalSlotIdx = slotId
                    ? (formDataReservation || []).findIndex((s) => s?.id === slotId)
                    : -1;
                  const ticketIndex = globalSlotIdx >= 0 ? globalSlotIdx : instanceIdx;
                  const ticketProducts = buildSlotProducts({
                    pr_list: eventData?.eventPricing?.prices || [],
                    selectedValueMap,
                    slotIds: slotId ? [slotId] : [],
                    ticketsDataSlot: ticketsData?.[ticketIndex],
                    slotId,
                    productsOpenAmount,
                    newProducts,
                    varients,
                    ticketIndex,
                    ticketsData,
                  });
                  return { ticket, products: ticketProducts, eventsDates };
                });
              })
          : // Standard (non-multidate) events — one entry per slot
            (formDataReservation || []).map((slot, slotIdx) => {
              const ticketVariant = (ticketTypeVariants || []).find(
                (t) => t._id === slot.ticket?.id && t.name?.toLowerCase() === slot.ticket?.title?.toLowerCase()
              ) || (ticketTypeVariants || []).find((t) => t._id === slot.ticket?.id);
              const ticket = { id: slot.ticket?.id, title: slot.ticket?.title };
              if (ticketVariant?.installmentStatus && ticketVariant?.installment?.value) {
                ticket.numberOfInstallments = ticketVariant.installment.value;
              }
              const selectedValueMap = selectedValue?.[0] || {};
              const ticketProducts = buildSlotProducts({
                pr_list: eventData?.eventPricing?.prices || [],
                selectedValueMap,
                slotIds: [slot.id],
                ticketsDataSlot: ticketsData?.[slotIdx],
                slotId: slot.id,
                productsOpenAmount,
                newProducts,
                varients,
                ticketIndex: slotIdx,
                ticketsData,
              });
              return { ticket, products: ticketProducts, eventsDates: [] };
            });

        const body = {
          eventId: eventData._id,
          paymentMethodId: currentPaymentMethod?._id,
          coverFee: convenienceFeeByPayment?.[activePayment] ?? true,
          isMember: isAuthenticated,
          registrationForm,
          checkoutDonation: { amount: donationAmount || 0 },
        };

        const res = await EventsService.calculateEventPrice(token, body);
        if (!cancelled) {
          const result = res?.result ?? res;
          setCalcResult(result);
          setApiFailed(false);
          onCalcPriceStatusChange && onCalcPriceStatusChange(false);
          onCalcDueAmountChange && onCalcDueAmountChange(result?.dueAmount);
        }
      } catch (e) {
        if (!cancelled) {
          setApiFailed(true);
          onCalcPriceStatusChange && onCalcPriceStatusChange(true);
          enqueueSnackbar("Failed to fetch prices", { variant: "error" });
        }
      } finally {
        if (!cancelled) setFetchingDiscount(false);
      }
    };
    fetchCalcPrice();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalPaymentWithCVFee, refetchTrigger, newProducts, varients, selectedValue, slotTicketKeys, ticketCounterKeys, productsOpenAmount, selectedEventDates, selectedVariants, donationAmount, numberOfTickets?.length, ticketTypeVariants?.[0]?.selectedQuantity]);

  useEffect(() => {
    // Process the input data and update the products state
    const processedData = processInputData(varients);
    const totalAmount = processedData?.reduce(
      (sum, obj) => sum + obj.amount,
      0,
    );
    const finalAmount = Number.isNaN(totalAmount) ? 0 : totalAmount;
    dispatch(eventsSlice.actions.setTotalProducts(finalAmount));
    setProducts(processedData);
  }, [dispatch, varients]);

  const processInputData = (data) => {
    const processedProducts =
      data && Object.values(data)?.length > 0
        ? Object.values(data)?.map((item, index) => {
            if (item.variantsInfo && item.variantsInfo.length > 0) {
              const totalCounterSum = item.variantsInfo.reduce(
                (sum, obj) => sum + obj.counterValue,
                0,
              );
              let totalAmount = 0;
              if (item.variantsInfo && item.variantsInfo.length > 0) {
                item.variantsInfo.forEach((variant) => {
                  totalAmount += variant.counterValue * (variant.price || variant.amount || 0);
                });
                return {
                  title: item.varientTitle,
                  total: totalCounterSum,
                  amount: totalAmount,
                  type: item.type,
                  priceId: item.id,
                };
              }
            } else {
              if (item.type === "openAmount") {
                return {
                  title: item.varientTitle,
                  total: item.counterValue || 0,
                  amount: item.price ? item.price * item.counterValue : 0,
                  price: item.price || 0,
                  type: item.type,
                  priceId: item.id,
                };
              } else {
                return {
                  title: item.varientTitle,
                  total: item.counterValue || 0,
                  amount: (item.price || item.amount || 0) * (item.counterValue || 0),
                  type: item.type,
                  priceId: item.id,
                };
              }
            }
          })
        : [];
    return processedProducts;
  };

  const calculateInstallmentAmount = (item, type = "ticket") => {
    if (!item?.installmentStatus) return "";
    if (item?.installment?.value) {
      const itemAmount =
        type === "ticket"
          ? item?.amount * item?.selectedQuantity
          : item?.amount;
      return numberFormat({
        number: +itemAmount / item?.installment?.value,
        decimal: 2,
        style: "currency",
      });
    }
    return "";
  };

  const enabledTicketInstallment = () => {
    const currentPrice = eventData?.eventPricing?.prices?.find(
      (price) => price?.type === "onRegistration",
    );
    return currentPrice || {};
  };
  const enableProductInstallment = (item) => {
    const currentPrice = eventData?.eventPricing?.prices?.find(
      (price) => price?._id === item?.priceId,
    );
    return currentPrice || {};
  };
  const getOpenAmountTotal = (product) => {
    if (!productsOpenAmount || !ticketsData) return product.amount;
    const priceId = product.priceId;
    const priceInfo = eventData?.eventPricing?.prices?.find((pr) => pr._id === priceId);
    if (!priceInfo || (priceInfo.type !== "openAmount" && !priceInfo.openAmount)) return product.amount;
    const productIndex = eventData?.eventPricing?.prices
      ?.filter((pr) => pr.type === "optional" || pr.type === "openAmount")
      ?.findIndex((pr) => pr._id === priceId);
    if (productIndex === -1) return product.amount;
    return (numberOfTickets || []).reduce((sum, ticketUUID, ticketIdx) => {
      const counter = ticketsData[ticketIdx]?.[productIndex]?.counter || 0;
      const price = Number(productsOpenAmount?.[ticketUUID]?.[productIndex] ?? priceInfo.minimumOpenAmount ?? 0);
      return sum + counter * price;
    }, 0);
  };

  const shouldShowConvenienceFeeToggle =
    currentPaymentMethod?.coverConvenienceFee;
  const isConvenienceFeeOptional =
    currentPaymentMethod?.convenienceFeeInfo?.optional === true;

  return (
    <>
      {(totalPay?.total !== "0.00" || totalProducts > 0 || isIndividual || totalDiscount > 0) && (
        <Paper
          sx={{
            minHeight: "278px",
            borderRadius: "10px",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#1A2027" : "#fff",
          }}
        >
          <Wrapper>
            <SummaryTitle>
              <span>Summary</span>
            </SummaryTitle>
            <DataFoundWrapper>
              {isIndividual && (() => {
                const onRegPrice = eventData?.eventPricing?.prices?.find(
                  (p) => p?.type === "onRegistration" && p?.variant
                );
                const rawVariantInfo = onRegPrice?.variantInfo || {};
                const hasMultiDateVariants = Object.values(rawVariantInfo).some((v) => v?.eventDate);
                const ticketCount = numberOfTickets?.length || 0;
                const ticketTitle = onRegPrice?.title || "Ticket";

                let totalAmount = 0;
                let hasAnySelection = false;

                if (hasMultiDateVariants) {
                  (selectedVariants || []).forEach((ticketVarMap) => {
                    Object.values(ticketVarMap || {}).forEach((variantKey) => {
                      if (variantKey !== null && variantKey !== undefined) {
                        hasAnySelection = true;
                        totalAmount += rawVariantInfo[variantKey]?.amount || 0;
                      }
                    });
                  });
                } else {
                  (selectedEventDates || []).forEach((ticketDates) => {
                    if (!Array.isArray(ticketDates)) return;
                    ticketDates.forEach((dateIdx) => {
                      hasAnySelection = true;
                      totalAmount += eventData?.multiEventsDates?.[dateIdx]?.amount || 0;
                    });
                  });
                }

                if (!hasAnySelection) return null;

                const ticketVariant = ticketTypeVariants?.[0];
                return (
                  <SummaryItem key="individual-dates">
                    <SummaryItemCharge>
                      <SummaryItemInfo>
                        <SummaryItemLedger>{ticketCount}× {ticketTitle}</SummaryItemLedger>
                      </SummaryItemInfo>
                      {totalAmount > 0 && (
                        <PaymentAmount>{numberFormat({ number: totalAmount, decimal: 2, style: "currency" })}</PaymentAmount>
                      )}
                    </SummaryItemCharge>
                    {isInstallmentEnabled &&
                      totalAmount > 0 &&
                      enabledTicketInstallment()?.isInstallmentTransaction && (
                        <PaymentInstallment
                          onAllowInstallmentChange={(e) => {
                            const { checked } = e.target;
                            const updatedTicketTypeVariants = ticketTypeVariants?.map((val) => ({
                              ...val,
                              installmentStatus: checked,
                              ...(!checked && { installment: null }),
                            }));
                            dispatch(setTicketTypeVariants(updatedTicketTypeVariants));
                            setActivePaymentAfter();
                            triggerRefetch();
                          }}
                          installmentValue={ticketVariant?.installmentStatus || false}
                          onSelectInstallment={(value) => {
                            const updatedTicketTypeVariants = ticketTypeVariants?.map((val) => ({
                              ...val,
                              installment: value,
                            }));
                            dispatch(setTicketTypeVariants(updatedTicketTypeVariants));
                            triggerRefetch();
                          }}
                          selectedInstallment={ticketVariant?.installment}
                          installmentAmount={
                            ticketVariant?.installmentStatus && ticketVariant?.installment?.value
                              ? numberFormat({
                                  number: totalAmount / ticketVariant.installment.value,
                                  decimal: 2,
                                  style: "currency",
                                })
                              : ""
                          }
                          maxInstallment={
                            enabledTicketInstallment()?.installmentDetailsTransaction?.numberOfInstallment
                          }
                        />
                      )}
                  </SummaryItem>
                );
              })()}
              {!eventData?.freeEvent &&
                !isIndividual &&
                !!ticketTypeVariants?.length &&
                ticketTypeVariants?.map(
                  (item, index) =>
                    item?.selectedQuantity > 0 && (
                      <SummaryItem>
                        <SummaryItemCharge>
                          <SummaryItemInfo>
                            <SummaryItemLedger>
                              {item?.selectedQuantity} {item?.name}
                            </SummaryItemLedger>
                          </SummaryItemInfo>
                          <PaymentAmount>{`${numberFormat({
                            number: +item?.amount * +item?.selectedQuantity,
                            decimal: 2,
                            style: "currency",
                          })}`}</PaymentAmount>
                        </SummaryItemCharge>
                        {isInstallmentEnabled &&
                          item?.amount > 0 &&
                          enabledTicketInstallment()
                            ?.isInstallmentTransaction && (
                            <PaymentInstallment
                              onAllowInstallmentChange={(e) => {
                                const { checked } = e.target;
                                const updatedTicketTypeVariants = ticketTypeVariants?.map(
                                  (val) => {
                                    if (val?.name === item?.name) {
                                      return {
                                        ...val,
                                        installmentStatus: checked,
                                        ...(!checked && { installment: null }),
                                      };
                                    }
                                    return val;
                                  },
                                );
                                dispatch(
                                  setTicketTypeVariants(
                                    updatedTicketTypeVariants,
                                  ),
                                );
                                setActivePaymentAfter();
                                triggerRefetch();
                              }}
                              installmentValue={
                                item?.installmentStatus || false
                              }
                              onSelectInstallment={(value) => {
                                const updatedTicketTypeVariants = ticketTypeVariants?.map(
                                  (val) => {
                                    if (val?.name === item?.name) {
                                      return {
                                        ...val,
                                        installment: value,
                                      };
                                    }
                                    return val;
                                  },
                                );
                                dispatch(
                                  setTicketTypeVariants(
                                    updatedTicketTypeVariants,
                                  ),
                                );
                                triggerRefetch();
                              }}
                              selectedInstallment={item?.installment}
                              installmentAmount={calculateInstallmentAmount(
                                item,
                                "ticket",
                              )}
                              maxInstallment={
                                enabledTicketInstallment()
                                  ?.installmentDetailsTransaction
                                  ?.numberOfInstallment
                              }
                            />
                          )}
                      </SummaryItem>
                    ),
                )}
              {newProducts.map((product, index) => {
                const displayAmount = getOpenAmountTotal(product);
                return (
                <SummaryItem>
                  {product?.total > 0 && displayAmount > 0 && (
                    <>
                      <SummaryItemCharge>
                        <SummaryItemInfo>
                          <SummaryItemLedger>
                            {product?.total} {product?.title}
                          </SummaryItemLedger>
                        </SummaryItemInfo>
                        {`${numberFormat({
                          number: displayAmount,
                          decimal: 2,
                          style: "currency",
                        })}`}
                      </SummaryItemCharge>
                      {isInstallmentEnabled &&
                        enableProductInstallment(product)
                          ?.isInstallmentTransaction && (
                          <PaymentInstallment
                            onAllowInstallmentChange={(e) => {
                              const { checked } = e.target;
                              const updatedNewProducts = newProducts?.map(
                                (val) => {
                                  if (val?.title === product?.title) {
                                    return {
                                      ...val,
                                      installmentStatus: checked,
                                      ...(!checked && { installment: null }),
                                    };
                                  }
                                  return val;
                                },
                              );
                              setProducts(updatedNewProducts);
                              setActivePaymentAfter();
                              triggerRefetch();
                            }}
                            installmentValue={
                              product?.installmentStatus || false
                            }
                            onSelectInstallment={(value) => {
                              const updatedNewProducts = newProducts?.map(
                                (val) => {
                                  if (val?.title === product?.title) {
                                    return {
                                      ...val,
                                      installment: value,
                                    };
                                  }
                                  return val;
                                },
                              );
                              setProducts(updatedNewProducts);
                              triggerRefetch();
                            }}
                            selectedInstallment={product?.installment}
                            installmentAmount={calculateInstallmentAmount(
                              product,
                              "product",
                            )}
                            maxInstallment={
                              enableProductInstallment(product)
                                ?.installmentDetailsTransaction
                                ?.numberOfInstallment
                            }
                          />
                        )}
                    </>
                  )}
                </SummaryItem>
                );
              })}
              {!fetchingDiscount && calcResult && +(calcResult.totalDiscount ?? calcResult.discount ?? 0) > 0 && (
                <SummaryItem>
                  <SummaryItemCharge>
                    <SummaryItemInfo>
                      <SummaryItemLedger>Total Discount</SummaryItemLedger>
                    </SummaryItemInfo>
                    <PaymentAmount>
                      -{numberFormat({ number: +(calcResult.totalDiscount ?? calcResult.discount), decimal: 2, style: "currency" })}
                    </PaymentAmount>
                  </SummaryItemCharge>
                </SummaryItem>
              )}
              {shouldShowConvenienceFeeToggle && !billMyAccount && calcResult && +calcResult.totalAmount > 0 && (
                <SummaryItem>
                  {convenienceFeeByPayment[activePayment] && (
                    <SummaryItemCharge>
                      <div>
                        <span style={{ color: FontColors.Blue002E5C, fontSize: Fonts.GeneralAmountFont.fontSize }}>
                          Convenience fee
                        </span>
                      </div>
                      <PaymentAmount>
                        {fetchingDiscount ? (
                          <SpinnerIcon $spinning={true} />
                        ) : numberFormat({
                          number: Math.max(0, +calcResult.convenienceFee),
                          decimal: 2,
                          style: "currency",
                        })}
                      </PaymentAmount>
                    </SummaryItemCharge>
                  )}
                  {isConvenienceFeeOptional && (
                    <div
                      className={`wrapper check-box__wrapper`}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "8px",
                        marginTop: "10px",
                        paddingTop: "10px",
                        color: FontColors.Slate6B7280,
                        borderTop: "1px dashed rgba(0, 46, 92, 0.10)",
                      }}
                    >
                      <label
                        className="check-box-container"
                        style={{
                          position: "relative",
                          display: "inline-block",
                          width: "18px",
                          height: "18px",
                          flexShrink: 0,
                        }}
                      >
                        <input
                          type="checkbox"
                          style={{ marginRight: 8 }}
                          checked={convenienceFeeByPayment[activePayment]}
                          onChange={(e) => {
                            setConvenienceFeeByPayment((prev) => ({
                              ...prev,
                              [activePayment]: e.target.checked,
                            }));
                          }}
                        />
                        <span className={"checkbox-checkmark"}></span>
                      </label>
                      <span className="text" style={{ lineHeight: "1.4", paddingLeft: "5px" }}>
                        {currentPaymentMethod?.convenienceFeeInfo?.message}
                      </span>
                    </div>
                  )}
                </SummaryItem>
              )}
              <TotalPayment>
                <p>Payment Total</p>
                <span>
                  {fetchingDiscount ? (
                    <SpinnerIcon $spinning={true} />
                  ) : apiFailed ? (
                    <RetryButton onClick={triggerRefetch}>↻ Retry</RetryButton>
                  ) : calcResult ? (
                    numberFormat({ number: Math.max(0, +calcResult.totalAmount), decimal: 2, style: "currency" })
                  ) : null}
                </span>
              </TotalPayment>
              <TotalDueAmount>
                <p>Due Now</p>
                <span>
                  {fetchingDiscount ? (
                    <SpinnerIcon $spinning={true} />
                  ) : apiFailed ? (
                    <RetryButton onClick={triggerRefetch}>↻ Retry</RetryButton>
                  ) : calcResult ? (
                    numberFormat({ number: Math.max(0, +calcResult.dueAmount), decimal: 2, style: "currency" })
                  ) : null}
                </span>
              </TotalDueAmount>
            </DataFoundWrapper>
          </Wrapper>
        </Paper>
      )}
    </>
  );
};
